<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: ChromosomeBrowser                                     |
| Section: components                                              |
| Purpose: Chromosome segment browser with match metric overlays.  |
+------------------------------------------------------------------+
-->
<template>
    <div class="chromosome-browser">
        <header class="browser-header" v-if="!loading && !error">
            <div class="summary-grid">
                <div class="summary-card" :class="{ 'summary-card--animating': isSummaryAnimating }">
                    <span class="summary-label">{{ $t('geneticMatches.chromosomeBrowser.metrics.totalCm') }}</span>
                    <strong>{{ formattedSummary.totalCm }}</strong>
                </div>
                <div class="summary-card" :class="{ 'summary-card--animating': isSummaryAnimating }">
                    <span class="summary-label">{{ $t('geneticMatches.chromosomeBrowser.metrics.totalSnps') }}</span>
                    <strong>{{ formattedSummary.totalSnps }}</strong>
                </div>
                <div class="summary-card" :class="{ 'summary-card--animating': isSummaryAnimating }">
                    <span class="summary-label">{{ $t('geneticMatches.chromosomeBrowser.metrics.chromosomesWithMatches')
                    }}</span>
                    <strong>{{ formattedSummary.chromosomeCount }}</strong>
                </div>
                <div class="summary-card" :class="{ 'summary-card--animating': isSummaryAnimating }">
                    <span class="summary-label">{{ $t('geneticMatches.chromosomeBrowser.metrics.sharedPercentage')
                    }}</span>
                    <strong>{{ formattedSummary.sharedPercentage }}</strong>
                </div>
            </div>
        </header>

        <div class="legend" v-if="!loading && !error">
            <span class="legend-item"><i class="legend-color" :class="sharedLegendClass" :style="sharedLegendStyle" />{{
                $t('geneticMatches.chromosomeBrowser.legend.sharedSegment') }}</span>
            <span class="legend-item"><i class="legend-color legend-color--none" />{{
                $t('geneticMatches.chromosomeBrowser.legend.noMatch') }}</span>
            <span class="legend-item"><i class="legend-color legend-color--unsampled" />{{
                $t('geneticMatches.chromosomeBrowser.legend.notSampled') }}</span>
        </div>

        <div v-if="showAncestryOverlay && !loading && !error" class="legend legend--ancestry">
            <span class="legend-item"><strong>{{ $t('geneticMatches.chromosomeBrowser.legend.ancestry')
                    }}:</strong></span>
            <span v-if="ancestryLoading" class="legend-item">{{ $t('geneticMatches.chromosomeBrowser.loadingAncestry')
                }}</span>
            <span v-else-if="!ancestryOverlay?.available" class="legend-item">{{
                $t('geneticMatches.chromosomeBrowser.noAncestry') }}</span>
            <span v-else-if="(ancestryOverlay.legend || []).length === 0" class="legend-item">{{
                $t('geneticMatches.chromosomeBrowser.noAncestry') }}</span>
            <span v-for="item in ancestryOverlay.legend || []" :key="item.ancestry"
                class="legend-item legend-item--interactive"
                :class="{ 'legend-item--active': activeLegendAncestry === item.ancestry }"
                @mouseenter="setHoveredLegendAncestry(item.ancestry)" @mouseleave="clearHoveredLegendAncestry"
                @focus="setHoveredLegendAncestry(item.ancestry)" @blur="clearHoveredLegendAncestry"
                @click="togglePinnedLegendAncestry(item.ancestry)"
                @keydown.enter.prevent="togglePinnedLegendAncestry(item.ancestry)"
                @keydown.space.prevent="togglePinnedLegendAncestry(item.ancestry)"
                @keydown.esc.prevent="clearPinnedLegendAncestry" tabindex="0" role="button"
                :aria-pressed="activeLegendAncestry === item.ancestry ? 'true' : 'false'">
                <i class="legend-color" :style="{ background: item.color, borderColor: item.color }" />{{
                    localizeAncestry(item.ancestry) }}
            </span>
        </div>

        <p v-if="loading" class="state-message">{{ $t('geneticMatches.chromosomeBrowser.loading') }}</p>
        <p v-else-if="error" class="state-message state-message--error">{{ error }}</p>
        <p v-else-if="segments.length === 0" class="state-message">{{ $t('geneticMatches.chromosomeBrowser.noSegments')
        }}</p>

        <div v-else class="chromosome-list">
            <div v-for="chromosome in chromosomeRows" :key="chromosome.number" class="chromosome-row">
                <span class="chromosome-label">{{ chromosome.number }}</span>
                <div class="chromosome-track-shell" :style="{ width: chromosome.trackWidthPercent + '%' }">
                    <div class="chromosome-track-base" />
                    <div v-if="chromosome.unsampledPercent > 0" class="chromosome-track-unsampled"
                        :style="{ width: chromosome.unsampledPercent + '%' }" />
                    <div v-for="(segment, index) in chromosome.segments"
                        :key="`${segmentAnimationCycle}-${chromosome.number}-${index}-${segment.startBp}-${segment.endBp}`"
                        class="segment-rect" :style="segmentStyle(segment)">
                        <button v-if="getAncestrySlices(segment).length === 0"
                            class="segment-slice segment-slice--plain"
                            :class="{ 'segment-slice--dimmed': isSliceDimmed(null) }"
                            @mouseenter="showTooltip($event, segment)" @mouseleave="hideTooltip"
                            @focus="showTooltip($event, segment)" @blur="hideTooltip"
                            :aria-label="segmentAriaLabel(segment)" type="button" />

                        <button v-for="(slice, sliceIndex) in getAncestrySlices(segment)"
                            :key="`${segment.startBp}-${segment.endBp}-${slice.ancestry}-${slice.startBp}-${slice.endBp}-${sliceIndex}`"
                            class="segment-slice segment-slice--ancestry"
                            :class="{ 'segment-slice--dimmed': isSliceDimmed(slice) }"
                            :style="segmentSliceStyle(segment, slice)" @mouseenter="showTooltip($event, segment, slice)"
                            @mouseleave="hideTooltip" @focus="showTooltip($event, segment, slice)" @blur="hideTooltip"
                            :aria-label="segmentAriaLabel(segment, slice)" type="button" />
                    </div>
                </div>
            </div>

            <div v-if="tooltip.visible" class="segment-tooltip"
                :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }">
                <p><strong>{{ formatOneDecimal(tooltip.segment.centimorgans) }} cM</strong></p>
                <p>{{ $t('geneticMatches.chromosomeBrowser.tooltip.snps') }}: {{ formatInteger(tooltip.segment.snps) }}
                </p>
                <p>
                    {{ $t('geneticMatches.chromosomeBrowser.tooltip.rangeMb') }}:
                    {{ formatOneDecimal(tooltip.segment.startMb) }}-{{ formatOneDecimal(tooltip.segment.endMb) }} Mb
                </p>
                <p v-if="tooltip.slice">
                    {{ $t('geneticMatches.chromosomeBrowser.tooltip.ancestry') }}:
                    <strong>{{ localizeAncestry(tooltip.slice.ancestry) }}</strong>
                </p>
                <p v-if="tooltip.slice">
                    {{ $t('geneticMatches.chromosomeBrowser.tooltip.overlap') }}:
                    {{ formatPercentage(tooltip.slice.overlapPercentWithinSegment) }}
                </p>
            </div>
        </div>

        <label class="ancestry-overlay-toggle" v-if="!loading && !error">
            <input type="checkbox" :checked="showAncestryOverlay"
                @change="emitAncestryOverlayToggle($event.target.checked)" />
            <span>{{ $t('geneticMatches.chromosomeBrowser.ancestryToggle') }}</span>
        </label>
    </div>
</template>

<script>
import {
    ACROCENTRIC_CHROMOSOMES,
    ACROCENTRIC_SHORT_ARM_BP,
    AUTOSOME_ORDER,
    CHROMOSOME_SIZES_HG19,
    MAX_AUTOSOME_BP_HG19,
} from '@/data/dna_segments/chromosomeSizesHg19';

export default {
    name: 'ChromosomeBrowser',
    emits: ['update:show-ancestry-overlay'],
    props: {
        selectedLabel: {
            type: String,
            default: '',
        },
        segments: {
            type: Array,
            default: () => [],
        },
        summary: {
            type: Object,
            default: () => ({
                totalCentimorgans: 0,
                totalSnps: 0,
                chromosomeCount: 0,
                sharedPercentage: 0,
            }),
        },
        loading: {
            type: Boolean,
            default: false,
        },
        error: {
            type: String,
            default: '',
        },
        showAncestryOverlay: {
            type: Boolean,
            default: false,
        },
        ancestryLoading: {
            type: Boolean,
            default: false,
        },
        ancestryOverlay: {
            type: Object,
            default: () => ({
                available: false,
                slicesBySegmentKey: {},
                legend: [],
            }),
        },
    },
    data() {
        return {
            tooltip: {
                visible: false,
                x: 0,
                y: 0,
                segment: null,
            },
            tweenedSummary: {
                totalCentimorgans: 0,
                totalSnps: 0,
                chromosomeCount: 0,
                sharedPercentage: 0,
            },
            // Track RAF id so we can cancel in-progress summary tweens safely.
            summaryAnimationFrame: null,
            segmentAnimationCycle: 0,
            isSummaryAnimating: false,
            hasInitializedSummary: false,
            hoveredLegendAncestry: null,
            pinnedLegendAncestry: null,
        };
    },
    computed: {
        sharedLegendClass() {
            return {
                'legend-color--shared': !this.shouldShowDiversityLegend,
                'legend-color--shared-diversity': this.shouldShowDiversityLegend,
            };
        },
        shouldShowDiversityLegend() {
            return Boolean(
                this.showAncestryOverlay
                && this.ancestryOverlay?.available
                && Array.isArray(this.ancestryOverlay?.legend)
                && this.ancestryOverlay.legend.length > 0
            );
        },
        sharedLegendStyle() {
            if (!this.shouldShowDiversityLegend) {
                return {};
            }

            const colors = this.ancestryOverlay.legend
                .map(item => item?.color)
                .filter(Boolean)
                .slice(0, 8);

            if (colors.length === 0) {
                return {};
            }

            const step = 100 / colors.length;
            const colorStops = colors
                .map((color, index) => {
                    const start = (index * step).toFixed(2);
                    const end = ((index + 1) * step).toFixed(2);
                    return `${color} ${start}%, ${color} ${end}%`;
                })
                .join(', ');

            return {
                borderColor: '#c9c9c9',
                background: `repeating-linear-gradient(135deg, ${colorStops})`,
            };
        },
        activeLegendAncestry() {
            return this.pinnedLegendAncestry || this.hoveredLegendAncestry;
        },
        chromosomeRows() {
            // Normalize every chromosome row to a shared reference width while keeping
            // per-chromosome base-pair coordinates for exact segment placement.
            const grouped = new Map();
            for (const segment of this.segments) {
                if (!grouped.has(segment.chromosome)) {
                    grouped.set(segment.chromosome, []);
                }
                grouped.get(segment.chromosome).push(segment);
            }

            return AUTOSOME_ORDER.map((number) => {
                const chromosomeLength = CHROMOSOME_SIZES_HG19[number];
                const trackWidthPercent = (chromosomeLength / MAX_AUTOSOME_BP_HG19) * 100;
                const unsampledBp = ACROCENTRIC_CHROMOSOMES.has(number) ? (ACROCENTRIC_SHORT_ARM_BP[number] || 0) : 0;
                const unsampledPercent = chromosomeLength > 0 ? (unsampledBp / chromosomeLength) * 100 : 0;

                return {
                    number,
                    chromosomeLength,
                    trackWidthPercent,
                    unsampledPercent,
                    segments: (grouped.get(number) || []).sort((a, b) => a.startBp - b.startBp),
                };
            });
        },
        formattedSummary() {
            return {
                totalCm: `${this.formatOneDecimal(this.tweenedSummary.totalCentimorgans)} cM`,
                totalSnps: this.formatInteger(this.tweenedSummary.totalSnps),
                chromosomeCount: this.formatInteger(this.tweenedSummary.chromosomeCount),
                sharedPercentage: this.formatPercentage(this.tweenedSummary.sharedPercentage),
            };
        },
    },
    watch: {
        summary: {
            immediate: true,
            deep: true,
            handler(nextSummary) {
                // Smooth metric transitions help users compare selected matches.
                this.animateSummary(nextSummary);
            },
        },
        selectedLabel(nextLabel, previousLabel) {
            if (!nextLabel || nextLabel === previousLabel) {
                return;
            }

            this.segmentAnimationCycle += 1;
            this.hideTooltip();
        },
    },
    beforeUnmount() {
        if (this.summaryAnimationFrame) {
            cancelAnimationFrame(this.summaryAnimationFrame);
            this.summaryAnimationFrame = null;
        }
    },
    methods: {
        emitAncestryOverlayToggle(nextValue) {
            this.$emit('update:show-ancestry-overlay', Boolean(nextValue));
        },
        formatOneDecimal(value) {
            return Number(value || 0).toFixed(1);
        },
        formatInteger(value) {
            return new Intl.NumberFormat(this.$i18n?.locale?.value || undefined).format(Math.round(value || 0));
        },
        formatPercentage(value) {
            const locale = this.$i18n?.locale?.value || undefined;
            const formatted = new Intl.NumberFormat(locale, {
                maximumFractionDigits: 2,
            }).format(Number(value || 0));

            return `${formatted}%`;
        },
        ancestryKey(ancestry) {
            return String(ancestry || '')
                .toLowerCase()
                .replace(/&/g, ' and ')
                .replace(/[^a-z0-9]+/g, '_')
                .replace(/^_+|_+$/g, '');
        },
        localizeAncestry(ancestry) {
            const key = this.ancestryKey(ancestry);
            const path = `geneticMatches.chromosomeBrowser.ethnicities.${key}`;
            if (this.$te && this.$te(path)) {
                return this.$t(path);
            }
            return ancestry;
        },
        normalizeSummary(summary) {
            return {
                totalCentimorgans: Number(summary?.totalCentimorgans || 0),
                totalSnps: Number(summary?.totalSnps || 0),
                chromosomeCount: Number(summary?.chromosomeCount || 0),
                sharedPercentage: Number(summary?.sharedPercentage || 0),
            };
        },
        easeOutCubic(progress) {
            return 1 - Math.pow(1 - progress, 3);
        },
        animateSummary(nextSummary) {
            // First paint is immediate; subsequent updates tween from current values.
            const target = this.normalizeSummary(nextSummary);

            if (this.summaryAnimationFrame) {
                cancelAnimationFrame(this.summaryAnimationFrame);
                this.summaryAnimationFrame = null;
            }

            if (!this.hasInitializedSummary) {
                this.tweenedSummary = { ...target };
                this.hasInitializedSummary = true;
                this.isSummaryAnimating = false;
                return;
            }

            const start = { ...this.tweenedSummary };
            const startTime = performance.now();
            const duration = 520;
            this.isSummaryAnimating = true;

            const step = (timestamp) => {
                const elapsed = timestamp - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const easedProgress = this.easeOutCubic(progress);

                this.tweenedSummary = {
                    totalCentimorgans: start.totalCentimorgans + ((target.totalCentimorgans - start.totalCentimorgans) * easedProgress),
                    totalSnps: start.totalSnps + ((target.totalSnps - start.totalSnps) * easedProgress),
                    chromosomeCount: start.chromosomeCount + ((target.chromosomeCount - start.chromosomeCount) * easedProgress),
                    sharedPercentage: start.sharedPercentage + ((target.sharedPercentage - start.sharedPercentage) * easedProgress),
                };

                if (progress < 1) {
                    this.summaryAnimationFrame = requestAnimationFrame(step);
                    return;
                }

                this.tweenedSummary = { ...target };
                this.summaryAnimationFrame = null;
                this.isSummaryAnimating = false;
            };

            this.summaryAnimationFrame = requestAnimationFrame(step);
        },
        segmentStyle(segment) {
            const chromosomeLength = CHROMOSOME_SIZES_HG19[segment.chromosome] || 1;
            const left = (segment.startBp / chromosomeLength) * 100;
            const width = Math.max(((segment.endBp - segment.startBp) / chromosomeLength) * 100, 0.4);
            return {
                left: `${left}%`,
                width: `${width}%`,
            };
        },
        segmentKey(segment) {
            return `${segment.chromosome}:${segment.startBp}:${segment.endBp}`;
        },
        getAncestrySlices(segment) {
            if (!this.showAncestryOverlay || !this.ancestryOverlay?.available) {
                return [];
            }

            // Overlay slices are keyed by exact chromosome/start/end coordinates.
            const map = this.ancestryOverlay?.slicesBySegmentKey || {};
            return Array.isArray(map[this.segmentKey(segment)]) ? map[this.segmentKey(segment)] : [];
        },
        segmentSliceStyle(segment, slice) {
            if (!slice) {
                return {
                    left: '0%',
                    width: '100%',
                };
            }

            const segmentLength = Math.max(segment.endBp - segment.startBp, 1);
            const left = ((slice.startBp - segment.startBp) / segmentLength) * 100;
            const width = Math.max(((slice.endBp - slice.startBp) / segmentLength) * 100, 0.6);

            return {
                left: `${left}%`,
                width: `${width}%`,
                background: slice.color,
            };
        },
        setHoveredLegendAncestry(ancestry) {
            if (this.pinnedLegendAncestry) {
                return;
            }
            this.hoveredLegendAncestry = ancestry || null;
        },
        clearHoveredLegendAncestry() {
            if (this.pinnedLegendAncestry) {
                return;
            }
            this.hoveredLegendAncestry = null;
        },
        togglePinnedLegendAncestry(ancestry) {
            if (this.pinnedLegendAncestry === ancestry) {
                this.pinnedLegendAncestry = null;
                this.hoveredLegendAncestry = null;
                return;
            }

            this.pinnedLegendAncestry = ancestry || null;
            this.hoveredLegendAncestry = null;
        },
        clearPinnedLegendAncestry() {
            this.pinnedLegendAncestry = null;
            this.hoveredLegendAncestry = null;
        },
        isSliceDimmed(slice) {
            const activeAncestry = this.activeLegendAncestry;
            if (!activeAncestry) {
                return false;
            }

            if (!slice) {
                return true;
            }

            return slice.ancestry !== activeAncestry;
        },
        segmentAriaLabel(segment, slice) {
            // Accessible description mirrors tooltip data for keyboard/screen-reader use.
            const base = `Chromosome ${segment.chromosome}, ${this.formatOneDecimal(segment.startMb)} to ${this.formatOneDecimal(segment.endMb)} Mb, ${this.formatOneDecimal(segment.centimorgans)} cM.`;
            if (!slice) {
                return base;
            }
            const overlap = this.formatPercentage(slice.overlapPercentWithinSegment);
            return `${base} ${slice.ancestry}, ${overlap}.`;
        },
        showTooltip(event, segment, slice = null) {
            const rect = event.currentTarget.getBoundingClientRect();
            this.tooltip = {
                visible: true,
                x: rect.left + rect.width / 2,
                y: rect.top - 10,
                segment,
                slice,
            };
        },
        hideTooltip() {
            this.tooltip.visible = false;
        },
    },
};
</script>

<style scoped>
.chromosome-browser {
    border-radius: var(--border-radius-xl);
    background: var(--brand-bg-1);
    box-shadow: var(--shadow-card-sm);
    padding: 28px;
    position: relative;
}

.browser-header {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    justify-content: space-between;
    align-items: flex-start;
}

.ancestry-overlay-toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #666;
    font-size: 13px;
    margin-top: 16px;
}

.ancestry-overlay-toggle input {
    accent-color: var(--brand-color-1);
}

.summary-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(120px, 1fr));
    gap: 10px;
    width: 100%;
}

.summary-card {
    display: flex;
    flex-direction: column;
    gap: 4px;
    border: 1px solid #ececec;
    border-radius: var(--border-radius-lg);
    padding: 10px 12px;
    background: var(--brand-bg-2);
    transition: transform 280ms ease, box-shadow 280ms ease, border-color 280ms ease;
}

.summary-card--animating {
    transform: translateY(-2px);
    border-color: color-mix(in srgb, var(--brand-color-1) 30%, #ececec);
    box-shadow: 0 10px 24px rgba(174, 30, 37, 0.08);
}

.summary-label {
    color: #888;
    font-size: 12px;
}

.legend {
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
    margin: 16px 0 18px;
}

.legend-item {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #666;
}

.legend-item--interactive {
    border-radius: var(--border-radius-pill);
    padding: 2px 6px;
    cursor: pointer;
    transition: var(--transition-ease);
}

.legend-item--interactive:hover,
.legend-item--interactive:focus {
    background: color-mix(in srgb, var(--brand-color-1) 10%, transparent);
    outline: none;
}

.legend-item--active {
    background: color-mix(in srgb, var(--brand-color-1) 14%, transparent);
}

.legend-color {
    width: 14px;
    height: 14px;
    border-radius: 4px;
    border: 1px solid #ddd;
    display: inline-block;
}

.legend-color--shared {
    background: var(--brand-color-1);
    border-color: var(--brand-color-1);
}

.legend-color--shared-diversity {
    border-color: #c9c9c9;
}

.legend-color--none {
    background: #ececec;
}

.legend-color--unsampled {
    background: repeating-linear-gradient(45deg,
            #d8d8d8,
            #d8d8d8 4px,
            #f2f2f2 4px,
            #f2f2f2 8px);
}

.state-message {
    color: #777;
    margin: 20px 0 6px;
}

.state-message--error {
    color: var(--brand-color-1);
}

.chromosome-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    /* margin-top: 6px; */
    position: relative;
}

.chromosome-row {
    display: flex;
    align-items: center;
    gap: 10px;
}

.chromosome-label {
    width: 20px;
    text-align: right;
    color: #666;
    font-size: 12px;
    flex-shrink: 0;
}

.chromosome-track-shell {
    position: relative;
    height: 16px;
    min-width: 40%;
    border-radius: var(--border-radius-pill);
    /* overflow: hidden; */
}

.chromosome-track-base {
    position: absolute;
    inset: 0;
    /* border-radius: var(--border-radius-pill); */
    background: #ececec;
}

.chromosome-track-unsampled {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    border-radius: var(--border-radius-pill) 0 0 var(--border-radius-pill);
    background: repeating-linear-gradient(45deg,
            #d8d8d8,
            #d8d8d8 4px,
            #f2f2f2 4px,
            #f2f2f2 8px);
}

.segment-rect {
    position: absolute;
    top: 0;
    bottom: 0;
    animation: segment-rect-enter 420ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

.segment-slice {
    position: absolute;
    top: 0;
    bottom: 0;
    border: none;
    border-radius: 0;
    opacity: 1;
    cursor: pointer;
    transform-origin: left center;
    transition: left 420ms cubic-bezier(0.2, 0.8, 0.2, 1), width 420ms cubic-bezier(0.2, 0.8, 0.2, 1),
        opacity 220ms ease, transform 220ms ease;
}

.segment-slice--plain {
    left: 0;
    width: 100%;
    background: var(--brand-color-1);
}

.segment-slice--ancestry {
    opacity: 0.96;
}

.segment-slice--dimmed {
    opacity: 0.2;
}

.segment-slice:hover,
.segment-slice:focus {
    outline: 4px solid rgba(174, 30, 37, 0.25);
    transform: translateY(-1px);
}

@keyframes segment-rect-enter {
    0% {
        opacity: 0;
        transform: scaleX(0.12);
    }

    100% {
        opacity: 1;
        transform: scaleX(1);
    }
}

.segment-tooltip {
    position: fixed;
    transform: translate(-50%, -100%);
    background: #1f1f1f;
    color: #fff;
    border-radius: 8px;
    padding: 8px 10px;
    z-index: 40;
    pointer-events: none;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
    min-width: 180px;
}

.segment-tooltip p {
    margin: 0;
    font-size: 12px;
    line-height: 1.4;
}

@media (max-width: 860px) {
    .chromosome-browser {
        padding: 20px;
    }

    .summary-grid {
        width: 100%;
        grid-template-columns: repeat(2, minmax(110px, 1fr));
    }

    .chromosome-list {
        overflow-x: auto;
        padding-bottom: 4px;
    }

    .chromosome-track-shell {
        min-width: 520px;
    }
}
</style>
